import type React from 'react';
import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import Button from '@polyai/ui/components/atoms/Button';
import { CheckIcon } from '@polyai/ui/components/atoms/Icons';

import type { Region } from 'screens/OpenTableRegion/types';
import FormProvider, { FormSelect } from '../../../components/molecules/Form';
import { TIMEZONES } from '../constants';
import { type TimezoneFormData, timezoneSchema } from '../types';

type Timezone = string;

type TimezoneDetails = {
  abbreviation: string;
  time: string;
};

const getTimezoneDetails = (timezone: string, date: Date): TimezoneDetails => {
  // Get abbreviation
  const options = {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  } as const;

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  const abbreviation = parts.find(p => p.type === 'timeZoneName')?.value || '';

  const time = parts
    .filter(p => p.type !== 'timeZoneName')
    .map(p => p.value)
    .join('');

  return { abbreviation, time };
};

const getTimezoneInfo = (
  date: Date,
  region: Region,
): [
  { label: Timezone; value: Timezone }[],
  Record<Timezone, TimezoneDetails>,
] => {
  const timezones = TIMEZONES[region] || TIMEZONES['united-states'];
  const timezoneDetails: Record<Timezone, TimezoneDetails> = {};

  timezones.forEach(timezone => {
    timezoneDetails[timezone] = getTimezoneDetails(timezone, date);
  });

  const timezoneOptions = timezones.map(timezone => ({
    label: timezone,
    value: timezone,
  }));

  return [timezoneOptions, timezoneDetails];
};

interface TimezoneOptionProps {
  timezone: Timezone;
  abbreviation: string;
  time: string;
  isSelected: boolean;
  isMenu: boolean;
}

const TimezoneOption: React.FC<TimezoneOptionProps> = ({
  timezone,
  abbreviation,
  time,
  isSelected,
  isMenu,
}) => {
  return (
    <div className="flex justify-between items-center">
      <div className="flex gap-xs2">
        <div className="text-body-regular font-semibold">{timezone}</div>
        <div className="text-body-regular text-secondary">{abbreviation}</div>
      </div>
      <div className="flex gap-xs2 items-center">
        <div className="text-body-regular text-helper w-md1 text-center">
          {time}
        </div>
        {isMenu && (
          <CheckIcon
            className={`text-icon-accent w-sm3 ${
              isSelected ? 'visible' : 'invisible'
            }`}
            size="sm"
          />
        )}
      </div>
    </div>
  );
};

interface TimezoneFormProps {
  onSubmit: (data: TimezoneFormData) => void;
  isSubmitting: boolean;
  timezone?: string;
  region: Region;
}

export default function TimezoneForm({
  onSubmit,
  isSubmitting,
  timezone,
  region,
}: TimezoneFormProps) {
  const methods = useForm<TimezoneFormData>({
    resolver: zodResolver(timezoneSchema),
    mode: 'onBlur',
    defaultValues: timezone
      ? { timezone: { label: timezone, value: timezone } }
      : undefined,
  });

  const [timezoneOptions, timezoneDetails] = useMemo(
    () => getTimezoneInfo(new Date(), region),
    [region],
  );

  const selectedTimezone = methods.watch('timezone');
  const { isValid } = methods.formState;

  // If region is UK or Ireland, auto-submit with first timezone
  // biome-ignore lint/correctness/useExhaustiveDependencies: Deliberate
  useEffect(() => {
    if (region === 'united-kingdom' || region === 'ireland') {
      onSubmit({
        timezone: { label: TIMEZONES[region][0], value: TIMEZONES[region][0] },
      });
    }
  }, [region]);

  // If region is UK or Ireland, do not render form since it's auto-submitted
  if (region === 'united-kingdom' || region === 'ireland') {
    return null;
  }

  return (
    <div className="flex flex-col gap-sm4">
      <div className="flex flex-col gap-sm2">
        <h2 className="text-header-large font-semibold text-primary leading-tight">
          Select your timezone
        </h2>
        <h3 className="text-body-regular text-secondary">
          Choose the timezone your business uses for opening hours and other
          time-based information
        </h3>
      </div>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col gap-sm4">
          <FormSelect
            className="text-body-regular"
            controlKey="timezone"
            data-test-id="select-timezone"
            formatOptionLabel={(
              option: { label: string; value: string },
              { context }: { context: string },
            ) => (
              <TimezoneOption
                abbreviation={timezoneDetails[option.value]?.abbreviation}
                isMenu={context === 'menu'}
                isSelected={selectedTimezone?.value === option.value}
                time={timezoneDetails[option.value]?.time}
                timezone={option.value}
              />
            )}
            help="Select the timezone for the agent"
            label="Timezone"
            options={timezoneOptions}
            placeholder="Start typing to search..."
            isSearchable
            withOptionSelectedIndicator
          />
          <Button
            disabled={isSubmitting || !isValid}
            label="Continue"
            type="submit"
            variant="PRIMARY"
            fullWidth
          />
        </div>
      </FormProvider>
    </div>
  );
}
